import { createSelector } from 'reselect';
import { Map, List } from 'immutable';
const HANDLING_BY_ID = () => false;
const MAKE_HANDLING_BY_ID = () => HANDLING_BY_ID;
const getReferencesForId = (referenceMap, entityMap, referenceId) => {
    if (!referenceMap.has(referenceId)) {
        return List();
    }
    return referenceMap
        .get(referenceId)
        .map(entityId => entityMap.get(entityId))
        .filter(a => a)
        .toList();
};
export const createSelectors = (reduxKeyPath, apiDescription, entityRelations, groupByKeys) => {
    const entityState = (state) => reduxKeyPath.split('.').reduce((reduction, current) => reduction[current], state);
    const idSelector = (_, props) => props.id;
    const referenceIdSelector = (_, props) => props.referenceId;
    const referenceIdsSelector = (_, props) => props.referenceIds;
    const referenceMap = createSelector(entityState, entityState => entityState.referenceMap);
    const entityMap = createSelector(entityState, entityState => entityState.entityMap);
    const fetchingSet = createSelector(entityState, entityState => entityState.fetchingSet);
    const updatingSet = createSelector(entityState, entityState => entityState.updatingSet);
    const deletingSet = createSelector(entityState, entityState => entityState.deletingSet);
    const getGroupByKeysSelectors = () => {
        if (groupByKeys) {
            const makeGetByGroupedIdFunc = () => {
                return createSelector(referenceMap, entityMap, referenceIdSelector, (referenceMap, entityMap, referenceId) => getReferencesForId(referenceMap, entityMap, referenceId));
            };
            const makeByMultipleGroupedIdFunc = () => {
                return createSelector(referenceMap, entityMap, referenceIdsSelector, (referenceMap, entityMap, referenceIds) => {
                    return Map(referenceIds.map(referenceId => {
                        return [referenceId, getReferencesForId(referenceMap, entityMap, referenceId)];
                    }));
                });
            };
            return {
                getByGroupedId: makeGetByGroupedIdFunc(),
                makeGetByGroupedId: makeGetByGroupedIdFunc,
                getByMultipleGroupedId: makeByMultipleGroupedIdFunc(),
                makeByMultipleGroupedId: makeByMultipleGroupedIdFunc,
            };
        }
        else {
            return {
                getByGroupedId: () => List(),
                makeGetByGroupedId: () => () => List(),
                getByMultipleGroupedId: () => Map(),
                makeByMultipleGroupedId: () => () => Map(),
            };
        }
    };
    const groupByKeysSelectors = getGroupByKeysSelectors();
    const makeGetByIdFunc = () => {
        return createSelector(entityMap, idSelector, (entityMap, id) => entityMap.get(id));
    };
    const getById = makeGetByIdFunc();
    const makeGetById = makeGetByIdFunc;
    // Fetching selectors
    const getFetchingSelectors = () => {
        if (apiDescription.fetch || apiDescription.fetchAll) {
            const makeFetchingByIdFunc = () => {
                return createSelector(fetchingSet, idSelector, (fetchingSet, id) => fetchingSet.includes(id));
            };
            return {
                fetchingById: makeFetchingByIdFunc(),
                makeFetchingById: makeFetchingByIdFunc,
            };
        }
        else {
            return {
                fetchingById: HANDLING_BY_ID,
                makeFetchingById: MAKE_HANDLING_BY_ID,
            };
        }
    };
    const fetchingSelectors = getFetchingSelectors();
    // Creating selectors
    const getCreatingSelectors = () => {
        if (apiDescription.create) {
            const makeCreatingFunc = () => {
                return createSelector(entityState, entityState => entityState.creating);
            };
            return {
                creating: makeCreatingFunc(),
                makeCreating: makeCreatingFunc,
            };
        }
        else {
            return {
                creating: HANDLING_BY_ID,
                makeCreating: MAKE_HANDLING_BY_ID,
            };
        }
    };
    const creatingSelectors = getCreatingSelectors();
    // Updating selectors
    const getUpdatingSelectors = () => {
        if (apiDescription.update) {
            const makeUpdating = () => {
                return createSelector(updatingSet, idSelector, (updatingSet, id) => updatingSet.includes(id));
            };
            return {
                updatingById: makeUpdating(),
                makeUpdatingById: makeUpdating,
            };
        }
        else {
            return {
                updatingById: HANDLING_BY_ID,
                makeUpdatingById: MAKE_HANDLING_BY_ID,
            };
        }
    };
    const updatingSelectors = getUpdatingSelectors();
    // Deleting selectors
    const getDeleteSelectors = () => {
        if (apiDescription.delete) {
            const makeDeleting = () => {
                return createSelector(deletingSet, idSelector, (deletingSet, id) => deletingSet.includes(id));
            };
            return {
                deletingById: makeDeleting(),
                makeDeletingById: makeDeleting,
            };
        }
        else {
            return {
                deletingById: HANDLING_BY_ID,
                makeDeletingById: MAKE_HANDLING_BY_ID,
            };
        }
    };
    const deleteSelectors = getDeleteSelectors();
    const getMakeRelationSelectorById = () => {
        if (entityRelations && entityRelations.length > 0) {
            return (entity, idsFromEntity) => () => {
                const getById = makeGetById();
                return createSelector(entity.selectors.entityMap, // A little hack to avoid circular type reference
                getById, (items, thisEntity) => idsFromEntity(thisEntity)
                    .map(id => items.get(id))
                    .filter(a => a));
            };
        }
        else {
            // allows to use makeRelationSelectorById if entityRelations are empty
            // so it doesn't have to be checked against undefined every time it's used
            return () => () => () => undefined;
        }
    };
    return {
        entityMap,
        ...groupByKeysSelectors,
        ...fetchingSelectors,
        ...creatingSelectors,
        ...updatingSelectors,
        ...deleteSelectors,
        getById,
        makeGetById,
        makeRelationSelectorById: getMakeRelationSelectorById(),
    };
};
